import { useMemo, useCallback } from 'react';
import { useOrganizationContext } from '@/contexts/OrganizationContext';
import { useAdmin } from './useAdmin';

export const PRODUCT_PAGES: Record<string, string[]> = {
  'fluity-chat': ['/chat', '/chat/performance'],
  'fluity-sender': ['/chat'],
  'fluity-crm': ['/chat'],
  'fluity-catalogo': ['/chat'],
  'fluity-shop': ['/chat'],
  'fluity-canais': ['/chat'],
};

export const MODULE_META: Record<string, { name: string; color: string; defaultPath: string }> = {
  'fluity-chat':   { name: 'Fluity Chat',   color: '#6366f1', defaultPath: '/chat' },
  'fluity-sender': { name: 'Fluity Sender', color: '#f59e0b', defaultPath: '/chat' },
  'fluity-crm':    { name: 'Fluity CRM',    color: '#10b981', defaultPath: '/chat' },
};

export const ADMIN_PAGES: string[] = [];

/** Returns the module key that owns the given path, or null. */
export function getModuleKeyForPath(path: string): string | null {
  const clean = path.split('?')[0].split('#')[0];
  for (const [key, pages] of Object.entries(PRODUCT_PAGES)) {
    if (pages.some(p => clean === p || clean.startsWith(p + '/'))) return key;
  }
  return null;
}

export interface ProductAccess {
  hasAccess: (path: string) => boolean;
  isModuleLocked: (moduleKey: string) => boolean;
  allowedPages: string[];
  userProducts: string[];
  isAdminMaster: boolean;
  isLoading: boolean;
}

export function useProductAccess(): ProductAccess {
  const { currentOrg } = useOrganizationContext();
  const { isAdmin: isAdminMaster, loading: adminLoading } = useAdmin();

  const userProducts: string[] = currentOrg?.enabled_modules ?? [];

  const allowedPages = useMemo(() => {
    if (isAdminMaster) {
      return Array.from(new Set([
        ...Object.values(PRODUCT_PAGES).flat(),
        ...ADMIN_PAGES,
        '/chat',
        '/profile',
      ])).sort();
    }
    const pages = new Set<string>(['/chat', '/profile']);
    userProducts.forEach(key => (PRODUCT_PAGES[key] || []).forEach(p => pages.add(p)));
    return Array.from(pages).sort();
  }, [userProducts, isAdminMaster]);

  /** True when the module is not in the org's enabled_modules. */
  const isModuleLocked = useCallback((moduleKey: string): boolean => {
    if (isAdminMaster) return false;
    return !userProducts.includes(moduleKey);
  }, [isAdminMaster, userProducts]);

  const hasAccess = useCallback((path: string): boolean => {
    if (isAdminMaster) return true;
    if (ADMIN_PAGES.includes(path)) return false;

    const commonPages = ['/chat', '/profile'];
    if (commonPages.includes(path)) return true;

    const moduleKey = getModuleKeyForPath(path);
    if (moduleKey !== null) return userProducts.includes(moduleKey);

    return false;
  }, [isAdminMaster, userProducts]);

  return useMemo(() => ({
    hasAccess,
    isModuleLocked,
    allowedPages,
    userProducts,
    isAdminMaster,
    isLoading: adminLoading,
  }), [hasAccess, isModuleLocked, allowedPages, userProducts, isAdminMaster, adminLoading]);
}
